import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetcher } from '@olagg/api-hooks';
import { AdminPushNotificationEndpoints, AdminSegmentEndpoints, AdminUserEndpoints } from '@olagg/api-hooks/endpoints/adminEndpoints';
import { AsyncSelect } from 'chakra-react-select';

export const CreatePushNotification = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [targetType, setTargetType] = useState<'segment' | 'user'>('segment');
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image: '',
    link: '',
    scheduled_at: '',
    status: 'draft' as 'draft' | 'scheduled',
  });

  const handleSearchSegments = async (inputValue: string) => {
    return await fetcher(
      AdminSegmentEndpoints.all({ page: 1, limit: 10, name: inputValue })
    ).then((response) => {
      const { segments } = response;
      return segments.map((segment: any) => ({
        value: segment.id,
        label: segment.name
      }));
    });
  };

  const handleSearchUsers = async (inputValue: string) => {
    return await fetcher(
      AdminUserEndpoints.all({ page: 1, limit: 10, query: inputValue })
    ).then((response) => {
      const { users } = response;
      return users.map((user: any) => ({
        value: user.id,
        label: `${user.name} (${user.email})`
      }));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: any = {
      title: formData.title,
      body: formData.body,
      image: formData.image || undefined,
      link: formData.link || undefined,
      status: formData.status,
    };

    if (targetType === 'segment') {
      if (!selectedSegment) {
        toast({
          title: 'Error',
          description: 'Debe seleccionar un segmento',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      payload.notification_segment_id = selectedSegment.value;
    } else {
      if (!selectedUser) {
        toast({
          title: 'Error',
          description: 'Debe seleccionar un usuario',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      payload.user_id = selectedUser.value;
    }

    if (formData.status === 'scheduled' && formData.scheduled_at) {
      payload.scheduled_at = new Date(formData.scheduled_at).toISOString();
    }

    try {
      await fetcher(AdminPushNotificationEndpoints.create(payload));
      toast({
        title: 'Éxito',
        description: 'Push notification creada correctamente',
        status: 'success',
        duration: 3000,
      });
      navigate('/push-notifications');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Error al crear la notificación',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Center w={'full'} p={4}>
      <VStack color={'white'} w={'full'} maxW={'800px'} spacing={6}>
        <Heading my={2}>Crear Push Notification</Heading>

        <Box w={'full'} bg={'gray.800'} p={6} borderRadius={'md'}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Título</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título de la notificación"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Mensaje</FormLabel>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Cuerpo del mensaje"
                  bg={'gray.700'}
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Imagen (URL)</FormLabel>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/image.png"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Link (URL)</FormLabel>
                <Input
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  placeholder="https://olagg.io/evento/123"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Enviar a:</FormLabel>
                <RadioGroup value={targetType} onChange={(val) => setTargetType(val as 'segment' | 'user')}>
                  <Stack direction="row">
                    <Radio value="segment">Segmento</Radio>
                    <Radio value="user">Usuario</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {targetType === 'segment' && (
                <FormControl isRequired>
                  <FormLabel>Segmento</FormLabel>
                  <AsyncSelect
                    value={selectedSegment}
                    onChange={setSelectedSegment}
                    loadOptions={handleSearchSegments}
                    placeholder="Buscar segmento por nombre..."
                    isClearable
                    chakraStyles={{
                      container: (provided) => ({ ...provided, bg: 'gray.700' }),
                      menuList: (provided) => ({ ...provided, bg: 'gray.700' }),
                      option: (provided, { isFocused }) => ({
                        ...provided,
                        bg: isFocused ? 'gray.600' : 'gray.700',
                      }),
                    }}
                  />
                </FormControl>
              )}

              {targetType === 'user' && (
                <FormControl isRequired>
                  <FormLabel>Usuario</FormLabel>
                  <AsyncSelect
                    value={selectedUser}
                    onChange={setSelectedUser}
                    loadOptions={handleSearchUsers}
                    placeholder="Buscar usuario por nombre, email o ID..."
                    isClearable
                    chakraStyles={{
                      container: (provided) => ({ ...provided, bg: 'gray.700' }),
                      menuList: (provided) => ({ ...provided, bg: 'gray.700' }),
                      option: (provided, { isFocused }) => ({
                        ...provided,
                        bg: isFocused ? 'gray.600' : 'gray.700',
                      }),
                    }}
                  />
                </FormControl>
              )}

              <FormControl isRequired>
                <FormLabel>Estado</FormLabel>
                <RadioGroup
                  value={formData.status}
                  onChange={(val) => setFormData({ ...formData, status: val as 'draft' | 'scheduled' })}
                >
                  <Stack direction="row">
                    <Radio value="draft">Borrador</Radio>
                    <Radio value="scheduled">Programar</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {formData.status === 'scheduled' && (
                <FormControl isRequired>
                  <FormLabel>Fecha y hora de envío</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    bg={'gray.700'}
                  />
                </FormControl>
              )}

              <Stack direction="row" spacing={4} w={'full'} justifyContent={'flex-end'} mt={4}>
                <Button variant={'outline'} onClick={() => navigate('/push-notifications')}>
                  Cancelar
                </Button>
                <Button type="submit" colorScheme={'purple'}>
                  Crear
                </Button>
              </Stack>
            </VStack>
          </form>
        </Box>
      </VStack>
    </Center>
  );
};
